import React, { useState } from 'react';
import { X, Check, XCircle, AlertCircle } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { noticeTierBadgeClass, noticeTierLabel, formatMediaDateRange } from '../../lib/media';

// RescheduleDecisionDialog — Ops/admin/shooter approval modal (spec §7 step 7).
//
// DOE rework: reschedules are fee-free. Approving moves the event to the new
// times — the backend conflict-checks the assigned shooter's calendar first,
// so a 409 ("shooter_conflict") lands in the error banner with the clash
// details. Rejecting closes the request and keeps the original date.

export default function RescheduleDecisionDialog({ request, onClose, onDecided, apiBase = '/api/admin/media/reschedule-requests' }) {
  const [decision, setDecision] = useState('approve');
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const body = { decision };
      if (decision === 'reject') {
        body.rejectionReason = rejectionReason.trim() || null;
      }
      const r = await api.post(`${apiBase}/${request.id}/decide`, body);
      onDecided?.(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err.message || 'Failed to submit decision'));
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[#0a0a0a] border border-white/[0.08] rounded-2xl shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
          <div>
            <h2 className="text-base font-semibold text-white tracking-tight">Reschedule request</h2>
            <p className="text-[12px] text-gray-500 mt-0.5 truncate" title={request.eventTitle}>
              {request.eventTitle} · {request.accountName}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-5 py-4 space-y-4">
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-[12px] text-red-300 flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-[12px]">
            <div className="rounded-lg border border-white/[0.05] bg-white/[0.02] px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Original</div>
              <div className="text-gray-300">{formatMediaDateRange(request.originalScheduledStartAt, request.originalScheduledEndAt)}</div>
            </div>
            <div className="rounded-lg border border-[#B0FC38]/30 bg-[#B0FC38]/[0.05] px-3 py-2">
              <div className="text-[10px] uppercase tracking-wider text-[#B0FC38] mb-0.5">Requested</div>
              <div className="text-white">{formatMediaDateRange(request.newScheduledStartAt, request.newScheduledEndAt)}</div>
              <div className="mt-1">
                <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${noticeTierBadgeClass(request.newNoticeTier)}`}>
                  {noticeTierLabel(request.newNoticeTier)}
                </span>
              </div>
            </div>
          </div>

          {request.reason && (
            <div className="text-[12px] text-gray-400">
              <span className="text-gray-500">Reason: </span>{request.reason}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setDecision('approve')}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-[13px] ${
                decision === 'approve'
                  ? 'border-[#B0FC38]/40 bg-[#B0FC38]/[0.08] text-[#B0FC38]'
                  : 'border-white/[0.08] text-gray-400 hover:text-white'
              }`}>
              <Check className="w-3.5 h-3.5" /> Approve
            </button>
            <button type="button" onClick={() => setDecision('reject')}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-[13px] ${
                decision === 'reject'
                  ? 'border-red-400/40 bg-red-500/[0.08] text-red-300'
                  : 'border-white/[0.08] text-gray-400 hover:text-white'
              }`}>
              <XCircle className="w-3.5 h-3.5" /> Reject
            </button>
          </div>

          {decision === 'approve' && (
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 text-[12px] text-gray-400">
              Approving moves the shoot to the requested times. The assigned shooter's calendar is
              checked for conflicts before anything changes.
            </div>
          )}

          {decision === 'reject' && (
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Rejection reason (optional)</span>
              <textarea rows="2" value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Visible to the school in their booking history."
                className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-[13px] focus:outline-none focus:border-[#B0FC38]/40" />
            </label>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.05]">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[12px] text-gray-400 hover:text-white">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className={`px-4 py-2 rounded-full text-[13px] font-medium disabled:opacity-50 ${
                decision === 'approve' ? 'bg-[#B0FC38] text-gray-900 hover:bg-[#9BE22E]' : 'bg-red-500 text-white hover:bg-red-600'
              }`}>
              {submitting ? 'Submitting…' : (decision === 'approve' ? 'Approve' : 'Reject')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
